<div class="container">
  <mic-breadcrumb id="administration-breadcrumb">
    <mic-breadcrumb-link link="/home/dashboard" icon="home"></mic-breadcrumb-link>
    <mic-breadcrumb-link label="Administration"></mic-breadcrumb-link>
  </mic-breadcrumb>

  <div class="flex items-center justify-between mt-3 mb-4">
    <h2 class="page-title m-0" id="administration-heading">Administration</h2>
    @if (selectedTabIndex === 0) {
    <div class="pr-4 action-buttons">
      <button
        id="reset-button"
        type="button"
        mat-stroked-button
        class="btn-cancel"
        (click)="resetChanges()"
        [disabled]="saving || loading || !hasUnsavedChanges"
      >
        <mat-icon>restart_alt</mat-icon> Reset Changes
      </button>
      <button
        id="save-button"
        type="button"
        mat-flat-button
        color="primary"
        class="btn-primary"
        (click)="save()"
        [disabled]="saving || loading || !hasUnsavedChanges"
      >
        <mat-icon>save</mat-icon> Save
      </button>
    </div>
    } @else if (selectedTabIndex === 1) {
    <div class="pr-4 action-buttons">
      <button
        id="external-api-reset-button"
        type="button"
        mat-stroked-button
        class="btn-cancel"
        (click)="resetExternalApiConfigChanges()"
        [disabled]="externalApiSaving || externalApiLoading || !hasExternalApiConfigChanges || !externalApiConfigAvailable"
      >
        <mat-icon>restart_alt</mat-icon> Reset Changes
      </button>
      <button
        id="external-api-save-button"
        type="button"
        mat-flat-button
        color="primary"
        class="btn-primary"
        (click)="saveExternalApiConfig()"
        [disabled]="
          externalApiSaving ||
          externalApiLoading ||
          !hasExternalApiConfigChanges ||
          !externalApiConfigAvailable ||
          externalApiConfigForm.invalid
        "
      >
        <mat-icon>save</mat-icon> Save
      </button>
    </div>
    }
  </div>

  <mat-tab-group
    [selectedIndex]="selectedTabIndex"
    mat-stretch-tabs="false"
    class="administration-tab-group"
    (selectedIndexChange)="onTabChange($event)"
  >
    <mat-tab label="Feature Flags">
      <ng-template matTabContent>
        <mat-accordion>
          <mat-expansion-panel expanded="true">
            <mat-expansion-panel-header>
              <mat-panel-title>
                <mic-icon class="mr-2" icon="flag"></mic-icon>
                Feature Flags
              </mat-panel-title>
            </mat-expansion-panel-header>
            <mat-divider></mat-divider>

            @if (loading) {
            <div class="loading-container">
              <mat-spinner diameter="36"></mat-spinner>
              <span class="loading-text">Loading feature flags...</span>
            </div>
            } @else if (featureFlagKeys.length === 0) {
            <div class="empty-state">
              <mat-icon>info_outline</mat-icon>
              <span>No feature flags available.</span>
            </div>
            } @else {
            <form [formGroup]="featureFlagsForm">
              <div class="feature-flags-grid mt-3">
                @for (key of featureFlagKeys; track key) {
                <div class="feature-flag-row">
                  <div class="feature-flag-text">
                    <p class="flag-label-text" [title]="key">
                      {{ formatFlagLabel(key) }}
                      @if (isFlagDirty(key)) {
                      <span class="flag-dirty-indicator">Modified</span>
                      }
                    </p>
                    <p class="flag-meta-text">{{ featureFlagsForm.get(key)?.value ? 'Enabled' : 'Disabled' }} · {{ key }}</p>
                  </div>
                  <mat-slide-toggle [id]="key + '-toggle'" [formControlName]="key"></mat-slide-toggle>
                </div>
                }
              </div>
            </form>
            }
          </mat-expansion-panel>
        </mat-accordion>
      </ng-template>
    </mat-tab>
    <mat-tab label="External API configuration">
      <ng-template matTabContent>
        <mat-accordion>
          <mat-expansion-panel expanded="true">
            <mat-expansion-panel-header>
              <mat-panel-title>
                <mic-icon class="mr-2" icon="settings"></mic-icon>
                External API configuration
              </mat-panel-title>
            </mat-expansion-panel-header>
            <mat-divider></mat-divider>

            @if (externalApiLoading) {
            <div class="loading-container">
              <mat-spinner diameter="36"></mat-spinner>
              <span class="loading-text">Loading external API config...</span>
            </div>
            } @else if (!externalApiConfigAvailable) {
            <div class="empty-state">
              <mat-icon>info_outline</mat-icon>
              <span>External API configuration has not been set up.</span>
            </div>
            } @else {
            <form class="external-api-config-form" [formGroup]="externalApiConfigForm">
              @if (externalApiBooleanFields.length) {
              <div class="external-api-boolean-fields mt-3">
                @for (field of externalApiBooleanFields; track field.key) {
                <div class="feature-flag-row">
                  <div class="feature-flag-text">
                    <p class="flag-label-text" [title]="field.key">
                      {{ field.label }}
                      @if (isExternalApiFieldDirty(field.key)) {
                      <span class="flag-dirty-indicator">Modified</span>
                      }
                    </p>
                    <p class="flag-meta-text">
                      {{ externalApiConfigForm.get(field.key)?.value ? 'Enabled' : 'Disabled' }} · {{ field.key }}
                    </p>
                  </div>
                  <mat-slide-toggle
                    [id]="field.key + '-toggle'"
                    [formControlName]="field.key"
                    [attr.aria-label]="field.label + ' toggle'"
                  ></mat-slide-toggle>
                </div>
                }
              </div>
              } @if (externalApiScalarFields.length) {
              <div class="external-api-scalar-fields">
                @for (field of externalApiScalarFields; track field.key) {
                <mat-form-field appearance="fill" class="edit-component w-full">
                  <mat-label class="custom-label-error">
                    {{ field.label }}
                    @if (isExternalApiFieldDirty(field.key)) {
                    <span class="flag-dirty-indicator">Modified</span>
                    }
                  </mat-label>
                  <input
                    [id]="field.key + '-input'"
                    matInput
                    [type]="field.type === 'number' ? 'number' : 'text'"
                    [formControlName]="field.key"
                    [min]="field.type === 'number' ? 0 : null"
                    [max]="field.type === 'number' ? maxExternalApiNumber : null"
                    [attr.aria-label]="field.label"
                  />
                  @if (field.type === 'number') { @if (externalApiConfigForm.get(field.key)?.hasError('min')) {
                  <mat-error>Value must be greater than or equal to 0.</mat-error>
                  } @if (externalApiConfigForm.get(field.key)?.hasError('max')) {
                  <mat-error>Value must be less than or equal to {{ maxExternalApiNumber }}.</mat-error>
                  } }
                </mat-form-field>
                }
              </div>
              } @if (externalApiArrayFields.length) {
              <div class="external-api-array-section">
                @for (field of externalApiArrayFields; track field.key) {
                <div class="external-api-array-group">
                  <section class="accessible-entities-section" [formArrayName]="field.key">
                    <div class="accessible-entity-add-row">
                      <mat-form-field appearance="fill" class="edit-component w-full">
                        <mat-label class="custom-label-error">
                          {{ field.label }}
                          @if (isExternalApiFieldDirty(field.key)) {
                          <span class="flag-dirty-indicator">Modified</span>
                          }
                        </mat-label>
                        <input
                          [id]="field.key + '-add-input'"
                          matInput
                          [value]="dynamicArrayInputs[field.key] ?? ''"
                          (input)="dynamicArrayInputs[field.key] = $any($event.target).value"
                          (keyup.enter)="addDynamicArrayItem(field.key)"
                          [attr.aria-label]="field.label + ' Add'"
                        />
                      </mat-form-field>
                      <button
                        type="button"
                        mat-flat-button
                        color="primary"
                        class="btn-primary accessible-entity-add-button"
                        (click)="addDynamicArrayItem(field.key)"
                        [disabled]="!(dynamicArrayInputs[field.key] ?? '').trim()"
                        [attr.aria-label]="'Add ' + field.label"
                      >
                        <mat-icon>add</mat-icon> {{ translationService.translate('addText') }}
                      </button>
                    </div>

                    @if (getExternalApiArray(field.key).length === 0) {
                    <div class="empty-state">
                      <mat-icon>info_outline</mat-icon>
                      <span>No {{ field.label.toLowerCase() }} configured.</span>
                    </div>
                    } @else {
                    <div class="accessible-entities-list">
                      @for (item of getExternalApiArray(field.key).controls; track $index) {
                      <div class="accessible-entity-row" [class.new-accessible-entity-row]="isNewDynamicArrayItem(field.key, item.value)">
                        <span class="accessible-entity-value">{{ item.value }}</span>
                        <div class="d-flex align-items-center gap-2">
                          <mat-checkbox
                            [id]="field.key + '-' + $index + '-enabled'"
                            [checked]="isDynamicArrayItemEnabled(field.key, $index)"
                            (change)="setDynamicArrayItemEnabled(field.key, $index, $event.checked)"
                            [attr.aria-label]="'Enable ' + item.value + ' for ' + field.label"
                          ></mat-checkbox>
                        </div>
                      </div>
                      }
                    </div>
                    }
                  </section>
                </div>
                } @if (hasUncheckedDynamicArrayItems) {
                <div class="unchecked-array-warning">
                  * Access permissions for unchecked entities will be removed when this configuration is saved
                </div>
                }
              </div>
              }
            </form>
            }
          </mat-expansion-panel>
        </mat-accordion>
      </ng-template>
    </mat-tab>

    <mat-tab label="Notifications Management">
      <ng-template matTabContent>
        <app-notifications-management></app-notifications-management>
      </ng-template>
    </mat-tab>
  </mat-tab-group>
</div>
